/**
 * PIQ 8.2.4: Coverage Audit Panel
 * ================================
 * Dev/admin-only panel showing which intelligence sources contributed this week.
 * Proves nothing is missing by listing all expected sources with counts.
 * 
 * Data comes ONLY from BrainContract - no additional API calls.
 */

import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Target,
  GitBranch,
  Package,
  AlertOctagon,
  TrendingUp,
  Shield,
  Megaphone,
  DollarSign,
  Zap,
  TrendingDown,
  Eye,
} from 'lucide-react';
import type { BrainContractSubset } from './operatorViewAdapter';

// ============================================================
// Types
// ============================================================

interface CoverageAuditPanelProps {
  brainContract: BrainContractSubset;
  /** Number of unified actions from Brain */
  actionsCount?: number;
  /** Number of pattern instances */
  patternsCount?: number;
  /** Show even in production (default: dev/admin only) */
  forceShow?: boolean;
  defaultExpanded?: boolean;
}

interface AuditLineItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  status: 'present' | 'missing' | 'na';
  count: number | null;
  detail?: string;
}

// ============================================================
// Helpers
// ============================================================

function getStatusIcon(status: AuditLineItem['status']) {
  switch (status) {
    case 'present':
      return <CheckCircle className="w-4 h-4 text-emerald-500" />;
    case 'missing':
      return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    case 'na':
      return <XCircle className="w-4 h-4 text-gray-400" />;
  }
}

function buildAuditItems(bc: BrainContractSubset, actionsCount: number, patternsCount: number): AuditLineItem[] {
  const items: AuditLineItem[] = [];

  // 1. Brain cases/actions
  items.push({
    id: 'actions',
    label: 'Brain Cases/Actions',
    icon: <Zap className="w-4 h-4 text-violet-500" />,
    status: actionsCount > 0 ? 'present' : 'missing',
    count: actionsCount,
    detail: actionsCount === 0 ? 'No actionable cases this week' : undefined,
  });

  // 2. Pattern instances
  items.push({
    id: 'patterns',
    label: 'Pattern Instances',
    icon: <TrendingDown className="w-4 h-4 text-indigo-500" />,
    status: patternsCount > 0 ? 'present' : 'missing',
    count: patternsCount,
    detail: patternsCount === 0 ? 'No patterns matched this week' : undefined,
  });

  // 3. Retailer attribution
  const hasAttribution = !!bc.retailerAttribution?.primaryDriver;
  items.push({
    id: 'attribution',
    label: 'Retailer Attribution (7.0)',
    icon: <Target className="w-4 h-4 text-violet-500" />,
    status: hasAttribution ? 'present' : 'missing',
    count: bc.retailerAttribution?.entries?.length || 0,
    detail: !hasAttribution ? 'No primary driver detected' : undefined,
  });

  // 4. Causality
  const hasCausality = !!bc.retailerCausality?.classification;
  items.push({
    id: 'causality',
    label: 'Cross-Retailer Causality (7.1)',
    icon: <GitBranch className="w-4 h-4 text-blue-500" />,
    status: hasCausality ? 'present' : 'missing',
    count: hasCausality ? 1 : 0,
    detail: !hasCausality ? 'Insufficient data for causality' : undefined,
  });

  // 5. Micro-attribution
  const microKeys = bc.microAttribution ? Object.keys(bc.microAttribution) : [];
  const hasMicro = microKeys.length > 0;
  const microSkuCount = microKeys.reduce((sum, k) => {
    return sum + (bc.microAttribution?.[k]?.topSkus?.length || 0);
  }, 0);
  items.push({
    id: 'micro',
    label: 'Micro-Attribution SKUs (7.3)',
    icon: <Package className="w-4 h-4 text-emerald-500" />,
    status: hasMicro ? 'present' : 'missing',
    count: microSkuCount,
    detail: !hasMicro ? 'No SKU-level drivers found' : undefined,
  });

  // 6. Early warnings
  const hasEarlyWarning = bc.earlyWarnings?.summary?.hasActiveWarning;
  const warningCount = bc.earlyWarnings?.active?.length || 0;
  items.push({
    id: 'early-warnings',
    label: 'Early Warnings (7.2)',
    icon: <AlertOctagon className="w-4 h-4 text-amber-500" />,
    status: hasEarlyWarning ? 'present' : 'na',
    count: warningCount,
    detail: !hasEarlyWarning ? 'No lead indicators this week (OK)' : undefined,
  });

  // 7. Propagation risk
  const hasPropagation = bc.propagation?.summary?.hasPropagationRisk;
  items.push({
    id: 'propagation',
    label: 'Propagation Risk (7.5)',
    icon: <TrendingUp className="w-4 h-4 text-red-500" />,
    status: hasPropagation ? 'present' : 'na',
    count: hasPropagation ? 1 : 0,
    detail: !hasPropagation ? 'No cascade risk detected (OK)' : undefined,
  });

  // 8. Containment playbook
  const hasContainment = !!bc.containmentPlaybook?.playbook;
  items.push({
    id: 'containment',
    label: 'Containment Playbook (7.6)',
    icon: <Shield className="w-4 h-4 text-orange-500" />,
    status: hasContainment ? 'present' : 'na',
    count: hasContainment ? 1 : 0,
    detail: !hasContainment ? 'Not needed (no propagation)' : undefined,
  });

  // 9. Preventive playbooks
  const preventiveCount = bc.preventivePlaybooks?.length || 0;
  items.push({
    id: 'preventive',
    label: 'Preventive Playbooks (7.4)',
    icon: <Shield className="w-4 h-4 text-blue-500" />,
    status: preventiveCount > 0 ? 'present' : 'na',
    count: preventiveCount,
    detail: preventiveCount === 0 ? 'No early warnings to prepare for' : undefined,
  });

  // 10. Google advisory
  const googleCount = bc.googleAdsAdvisory?.length || 0;
  items.push({
    id: 'google-ads',
    label: 'Google Ads Recommendations',
    icon: <Megaphone className="w-4 h-4 text-blue-500" />,
    status: googleCount > 0 ? 'present' : 'missing',
    count: googleCount,
    detail: googleCount === 0 ? 'No Google API recommendations available' : undefined,
  });

  // 11. Value ledger
  const hasLedger = !!bc.valueLedger?.ledgerId;
  items.push({
    id: 'value-ledger',
    label: 'Value Ledger (7.7)',
    icon: <DollarSign className="w-4 h-4 text-emerald-500" />,
    status: hasLedger ? 'present' : 'missing',
    count: hasLedger ? 1 : 0,
    detail: !hasLedger ? 'No ledger generated this week' : undefined,
  });

  return items;
}

// ============================================================
// Component
// ============================================================

export function CoverageAuditPanel({
  brainContract,
  actionsCount = 0,
  patternsCount = 0,
  forceShow = false,
  defaultExpanded = false,
}: CoverageAuditPanelProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Only show in dev/admin mode unless forceShow
  const isDev = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname.includes('preview') ||
    window.location.search.includes('admin=true')
  );

  if (!forceShow && !isDev) {
    return null;
  }

  const items = buildAuditItems(brainContract, actionsCount, patternsCount);
  const presentCount = items.filter(i => i.status === 'present').length;
  const missingCount = items.filter(i => i.status === 'missing').length;
  const totalCount = items.length;

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Coverage Audit</span>
          <span className="text-xs text-gray-500">
            ({presentCount}/{totalCount} sources)
          </span>
          {missingCount > 0 && (
            <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
              {missingCount} missing
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {/* Content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
            {items.map((item) => (
              <div
                key={item.id}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${
                  item.status === 'present'
                    ? 'bg-emerald-50 border-emerald-100'
                    : item.status === 'missing'
                    ? 'bg-amber-50 border-amber-100'
                    : 'bg-gray-50 border-gray-100'
                }`}
              >
                {getStatusIcon(item.status)}
                {item.icon}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700 truncate">
                      {item.label}
                    </span>
                    {item.count !== null && item.count > 0 && (
                      <span className="text-xs text-gray-500">({item.count})</span>
                    )}
                  </div>
                  {item.detail && (
                    <p className="text-xs text-gray-500 truncate">{item.detail}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
            Data sources from BrainContract only • No additional API calls
          </div>
        </div>
      )}
    </div>
  );
}

export default CoverageAuditPanel;
